import React, { useEffect, useRef, useState } from "react";
import api from "../api/client";
import { TOKENS } from "../tokens";

function digitsOnly(phone) {
  return String(phone || "").replace(/\D/g, "");
}

function looksLikeMobile(phone) {
  const d = digitsOnly(phone);
  if (d.length === 10 && /^[6-9]/.test(d)) return true;
  if (d.length === 12 && /^91[6-9]\d{9}$/.test(d)) return true;
  return false;
}

/**
 * Mobile input that verifies WhatsApp the same way as crm.goldpe.tech:
 * waits until the number looks complete, then checks (debounced + on blur).
 */
export function WhatsAppPhoneField({
  label = "PHONE / WHATSAPP",
  value,
  onChange,
  onWhatsApp,
  onStatus,
  required = false,
  placeholder = "+91 98765 43210",
  inputStyle,
}) {
  const [status, setStatus] = useState("idle"); // idle | checking | yes | no
  const onWhatsAppRef = useRef(onWhatsApp);
  onWhatsAppRef.current = onWhatsApp;
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  const valueRef = useRef(value);
  valueRef.current = value;

  const applyStatus = (next, yes) => {
    setStatus(next);
    onStatusRef.current?.(next);
    if (next === "yes") onWhatsAppRef.current?.(true);
    else if (next === "no") onWhatsAppRef.current?.(false);
    else onWhatsAppRef.current?.(false);
  };

  const runCheck = async (phone) => {
    if (!looksLikeMobile(phone)) {
      applyStatus("idle", false);
      return;
    }
    applyStatus("checking", false);
    let yes = true;
    try {
      const res = await api.post("/employees/whatsapp-check", { phone });
      yes = !!res.data?.hasWhatsapp;
    } catch (_) {
      yes = looksLikeMobile(phone);
    }
    if (valueRef.current !== phone) return;
    applyStatus(yes ? "yes" : "no", yes);
  };

  useEffect(() => {
    if (!looksLikeMobile(value)) {
      applyStatus("idle", false);
      return undefined;
    }
    applyStatus("checking", false);
    const t = setTimeout(() => { runCheck(value); }, 600);
    return () => clearTimeout(t);
  }, [value]);

  const borderColor = status === "yes" ? "#86EFAC" : status === "no" ? `${TOKENS.danger}66` : TOKENS.border;
  const bg = status === "yes" ? "#F0FDF4" : status === "no" ? TOKENS.dangerBg : TOKENS.cream;

  return (
    <div>
      {label ? (
        <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
          {label}{required ? " *" : ""}
        </label>
      ) : null}
      <div style={{ position: "relative" }}>
        <input
          type="tel"
          inputMode="tel"
          placeholder={placeholder}
          value={value}
          required={required}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => runCheck(value)}
          style={{
            width: "100%",
            padding: "9px 40px 9px 11px",
            borderRadius: 10,
            border: `1.5px solid ${borderColor}`,
            fontSize: 13,
            background: bg,
            outline: "none",
            color: TOKENS.ink,
            boxSizing: "border-box",
            ...inputStyle,
          }}
        />
        {status === "checking" && (
          <span
            title="Checking WhatsApp…"
            style={{
              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
              width: 12, height: 12, borderRadius: "50%",
              border: "2px solid #BBF7D0", borderTopColor: "#16A34A",
              animation: "spin 0.7s linear infinite",
            }}
          />
        )}
        {status === "yes" && (
          <span
            title="WhatsApp number"
            style={{
              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              width: 22, height: 22, borderRadius: 6, background: "#25D366",
              color: "#fff", fontSize: 11, fontWeight: 800,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            WA
          </span>
        )}
        {status === "no" && (
          <span
            title="No WhatsApp on this number"
            style={{
              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              background: TOKENS.danger, color: "#fff", fontSize: 9, fontWeight: 800,
              padding: "2px 6px", borderRadius: 999,
            }}
          >
            No WA
          </span>
        )}
      </div>
      {status === "checking" && (
        <div style={{ fontSize: 10, fontWeight: 600, color: TOKENS.muted, marginTop: 4 }}>Checking WhatsApp…</div>
      )}
      {status === "yes" && (
        <div style={{ fontSize: 10, fontWeight: 700, color: "#15803D", marginTop: 4 }}>WhatsApp number verified</div>
      )}
      {status === "no" && (
        <div style={{ fontSize: 10, fontWeight: 700, color: TOKENS.danger, marginTop: 4 }}>
          Not a WhatsApp number — daily status will not be sent to this mobile.
        </div>
      )}
    </div>
  );
}
